import { FileDownloadOutlined } from '@mui/icons-material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  tablePaginationClasses,
  Typography,
  Modal,
  TextField,
  Grid,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { visuallyHidden } from '@mui/utils'
import axios from 'axios'
import React, { useMemo, useState, useContext, useEffect } from 'react'
import { getComparator, getStatusChipStyles } from '../../libs/utils/table'
import { StyledTableContainer, TablePaginationActions } from './styledElements'
import { useSelector } from 'react-redux'
import { AuthContext } from '../../libs/contexts/authContext'
import { createHubspotTicket } from '../../store/actions/authActions'
import { useDispatch } from 'react-redux'
import CSTicketModal from './CSTicketModal'

const ApplicationManagementTable = ({
  rowsData,
  filterBy,
  boxFilters,
  productInQuestion,
  displayingFtapps,
  products,
}) => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [order, setOrder] = useState('desc')
  const [anchorEl, setAnchorEl] = useState(null)
  const [modalOpen, setModalOpen] = useState(rowsData.reduce((acc, curr) => ({ ...acc, [curr.arcId]: false }), {}))
  const [openCSModal, setOpenCSModal] = useState(false)
  const [currArcProdId, setCurrArcProdId] = useState('')
  const {
    agentObject: { firstName, lastName },
  } = useContext(AuthContext)
  const [agentName, setAgentName] = useState('')
  const [ticketARC, setTicketARC] = useState('')
  const affiliateCode = useSelector((state) => state.auth.affiliateCode)
  const email = useSelector((state) => state.auth.email)
  const dispatch = useDispatch()

  useEffect(() => {
    setAgentName(`${firstName} ${lastName}`)
  }, [firstName, lastName])

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleActionsStart = (e, arcId) => {
    setAnchorEl(e.currentTarget)
    setModalOpen((prev) => ({ ...prev, [arcId]: !prev[arcId] }))
  }

  const handleClose = (arcId) => {
    setModalOpen((prev) => ({ ...prev, [arcId]: false }))
  }
  const handleContinueApp = (arcId) => {
    setAnchorEl(null)
    setModalOpen((prev) => ({ ...prev, [arcId]: false }))
  }

  const handleUWReport = async (arcId, productId) => {
    const data = {
      arcId,
      purpose: '963uwreport',
      productId,
    }
    const url = `https://decline-agent-uwr-lifeeapp.afficiency.com/?arcId=${arcId}`

    try {
      const res = await axios.post(url, data)
      console.log(res.data) // Handle the response as needed
    } catch (error) {
      console.error('Error:', error)
      // Handle the error appropriately
    }
  }

  const handleSupportSubmit = async (desc) => {
    const data = {
      arcID: ticketARC,
      agentName: agentName,
      agentEmail: email,
      description: desc,
      productId: currArcProdId,
      affiliateCode: affiliateCode,
    }
    dispatch(createHubspotTicket(data))
    setOpenCSModal(false)
  }

  const handleNotifyClient = async (arcId) => {
    const data = {
      prsCode: '9605905',
      arcId,
      notificationChannel: 1,
    }
    const res = await axios.post('https://afficiency-live.whitetac.com/arcCenter', data)
  }

  const getProductUrl = (productId) => {
    const found = products.find((p) => p.productId === productId)
    if (!found) {
      const currentUrl = window.location.href
      if(currentUrl.includes('local') || currentUrl.includes('test')) {
        return `https://eapp-qa.afficiency.com/?productId=${productId}`
      } else if(currentUrl.includes('demo')) {
        return `https://eapp-demo.afficiency.com/?productId=${productId}`
      }else{
        return `https://lifeeapp.afficiency.com/?productId=${productId}`
      }
    }
    return found.productUrl
  }

  let rowsDataClone = rowsData.slice()
  Object.entries(filterBy).forEach(([k, v]) => {
    if (v === 'Quility Level Term and ROP') {
      rowsDataClone = rowsDataClone.filter((r) => r.productId === '8140' || r.productId === '8142' || r.productId === '81401')
    } else if (v !== 'All') {
      rowsDataClone = rowsDataClone.filter((r) =>
        k === 'productName' ? r.productId === productInQuestion.productId : r[k] === v
      )
    }
  })
  if (displayingFtapps) {
    Object.entries(boxFilters).forEach(([k, v]) => {
      if (v !== '') {
        rowsDataClone = rowsDataClone.filter((r) => r[v] === 1)
      }
    })
  }
  const visibleRows = useMemo(
    () =>
      rowsDataClone
        .sort(getComparator(order, 'applicationDate'))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, page, rowsPerPage, rowsDataClone]
  )

  return (
    <Box mt={2} overflow="auto">
      <CSTicketModal
        open={openCSModal}
        setOpen={setOpenCSModal}
        defaultTicketARC={ticketARC}
        setTicketArc={setTicketARC}
        defaultAgentName={agentName}
        setAgentName={setAgentName}
        defaultAffiliateCode={affiliateCode}
        handleSupportSubmit={handleSupportSubmit}
      />
      <StyledTableContainer component={Paper}>
        <Table aria-label="responsive table">
          <TableHead>
            <TableRow>
              <TableCell>ARCID</TableCell>
              <TableCell>
                <TableSortLabel active direction={order} onClick={() => setOrder(order === 'asc' ? 'desc' : 'asc')}>
                  Date
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                </TableSortLabel>
              </TableCell>
              <TableCell>Product Name</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>App Status</TableCell>
              <TableCell>Term</TableCell>
              <TableCell>Coverage</TableCell>
              <TableCell>Premium</TableCell>
              <TableCell>Policy #</TableCell>
              <TableCell>Ticket Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell>{row.arcId}</TableCell>
                <TableCell>{row.applicationDate}</TableCell>
                <TableCell>{row.productName}</TableCell>
                <TableCell>{row.details}</TableCell>
                <TableCell>
                  <Chip label={row.appStatus} sx={getStatusChipStyles(row.appStatus)} />
                </TableCell>
                <TableCell>{row.status}</TableCell>
                <TableCell>{row.term}</TableCell>
                <TableCell>{row.coverage && `$${row.coverage?.toLocaleString('en-us')}`}</TableCell>
                <TableCell>{row.paymentPremium}</TableCell>
                <TableCell>{row.policyNo}</TableCell>
                <TableCell>{row.ticketStatus}</TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    aria-label="more options"
                    size="small"
                    onClick={(e) => handleActionsStart(e, row.arcId)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Popover
                    open={modalOpen[row.arcId] || false}
                    anchorEl={anchorEl}
                    onClose={(e) => handleClose(row.arcId)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                  >
                    <List>
                      {row.appStatus === 'In Progress' &&
                        Date.parse(row.applicationDate) > Date.parse('Dec 8, 2023') &&
                        row.productId.includes('814') && (
                          <ListItem>
                            <ListItemButton
                              target="_blank"
                              href={`${getProductUrl(row.productId)}&arcId=${row.arcId}`}
                              onClick={(e) => handleContinueApp(row.arcId)}
                            >
                              <ListItemText primary="Continue Application" />
                            </ListItemButton>
                          </ListItem>
                        )}
                      {/* {(appStatus === 'Completed' || appStatus === 'Completed ') && (
            <ListItem button onClick={handleClose}>
              <Typography>View Application Details</Typography>
            </ListItem>
          )} */}
                      {row.status === 'Declined' && (row.productId === '8118' || row.productId.includes('8140')) && row.appStatus === 'Completed' && (
                        <ListItem>
                          <ListItemButton
                            target="_blank"
                            href={(window.location.href.includes('local') || window.location.href.includes("test")) ? `https://decline-lifeeapp-live.afficiency.com/?arcId=${row.arcId}` : row.declineUWRAgentURL}
                            onClick={() => handleUWReport(row.arcId, row.productId)}
                          >
                            <ListItemText primary="View U/W Report" />
                          </ListItemButton>
                        </ListItem>
                      )}
                      <ListItem>
                        <ListItemButton
                          target="_blank"
                          onClick={() => {
                            // handleContactSupport(row.arcId)
                            setTicketARC(row.arcId)
                            setCurrArcProdId(row.productId)
                            setOpenCSModal(true)
                          }}
                        >
                          <ListItemText primary="Open a customer support ticket" />
                        </ListItemButton>
                      </ListItem>
                      {/* {appStatus === 'In Progress' && (
            <ListItem button onClick={handleNotifyClient}>
              <Typography>Notify Client</Typography>
            </ListItem>
          )} */}
                    </List>
                  </Popover>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        count={rowsDataClone.length}
        rowsPerPage={rowsPerPage}
        page={page}
        component="div"
        sx={{
          mt: 2,
          [`.${tablePaginationClasses.spacer}`]: {
            display: 'none',
          },
          [`.${tablePaginationClasses.select}`]: {
            padding: '8px 10px',
            justifySelf: 'flex-start',
          },
          [`.${tablePaginationClasses.selectLabel}`]: {
            display: 'none',
          },
          [`.${tablePaginationClasses.displayedRows}`]: {
            opacity: 0,
          },
          [`.${tablePaginationClasses.toolbar}`]: {
            display: { lg: 'grid', md: 'grid', sm: 'flex', xs: 'flex' },
            gridGap: 5,
            gridTemplateColumns: '70px 150px 1fr',
            flexDirection: 'column',
            p: 0,
          },
          [`.${tablePaginationClasses.actions}`]: {
            // border: "3px solid orange",
          },
        }}
        SelectProps={{
          variant: 'outlined',
          style: {
            width: 'fit-content',
            margin: 0,
          },
          native: true,
        }}
        // eslint-disable-next-line react/no-unstable-nested-components, no-unused-vars
        labelDisplayedRows={({ ...info }) => (
          <Button
            variant="outlined"
            sx={{
              borderRadius: 5,
              border: '1px solid #CBD2DB',
              color: '#465464',
              cursor: 'auto',
            }}
            startIcon={<FileDownloadOutlined />}
          >
            export csv
          </Button>
        )}
        onPageChange={(event, p) => setPage(p)}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ActionsComponent={TablePaginationActions}
      />
    </Box>
  )
}

export default ApplicationManagementTable

const appManagementStyle = {
  nextButton: {
    textTransform: 'none',
    width: { lg: '50%', md: '50%', sm: '50%', xs: '60%' },
    height: '40px',
    backgroundColor: '#3C4FB9',
    color: 'white',
    borderRadius: '100px',
    mt: '20px',
    '&:hover': {
      backgroundColor: '#9BA5DF',
      color: '#36414E',
    },
    '&:disabled': {
      backgroundColor: '#C4CAF1',
      color: 'white',
      textTransform: 'none',
    },
  },

  textField: {
    backgroundColor: 'white',
    width: '100%',
  },

  textArea: {
    backgroundColor: 'white',
    width: '100%',
  },

  whiteBox: {
    display: 'flex',
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    paddingX: '20px',
    paddingTop: '40px',
    paddingBottom: '60px',
    width: { lg: '844px', md: '730px', sm: '730px', xs: '303px' },
    height: { lg: '438px', md: '438px', sm: '438px', xs: '500px' },
    background: '#FFFFFF',
    borderRadius: '8px',
    top: '40%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
}
